import type { BulkAddValidationResult } from '@openx/components-core/lib/List/types.ts';
import type {
  UseSeatTransferListBulkValidateParams,
  UseSeatTransferListBulkValidateReturnType,
} from '@openx/components-core/lib/SearchTransferList/variants/SeatTransferList/types.ts';
import { useQueryClient } from '@tanstack/react-query';
import groupBy from 'lodash/groupBy';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../components/AuthContext/context.ts';
import {
  OptionsByPathDocument,
  type OptionsByPathQuery,
  type OptionsByPathQueryVariables,
} from '../../../../../graphql/query/options/OptionsByPath.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../../modules/graphql/types.ts';
import type { CuratedDealBuyerDiscountSeat } from '../../types.ts';
import { seatOptionsToCuratedDealBuyerDiscountSeats } from '../../utils/seatOptionsToCuratedDealBuyerDiscountSeats.ts';

type ValidatedItems = {
  acceptedItems: CuratedDealBuyerDiscountSeat[];
  rejectedItems: string[];
};

export const useSeatTransferListBulkValidate = ({
  isItemBlocked,
  itemsToIgnore,
}: UseSeatTransferListBulkValidateParams<CuratedDealBuyerDiscountSeat>): UseSeatTransferListBulkValidateReturnType<CuratedDealBuyerDiscountSeat> => {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const { t } = useTranslation();

  const accountId = session?.exchange_session?.account?.id;

  const handleBulkValidate = useCallback(
    async (receivedSeatIds: string[]): Promise<BulkAddValidationResult<CuratedDealBuyerDiscountSeat>> => {
      const data = await getGraphQLRequestFromClient(queryClient)<OptionsByPathQuery, OptionsByPathQueryVariables>(
        OptionsByPathDocument,
        {
          filter: {
            seat_id__in: receivedSeatIds.join(','),
            used_by_curateddealbuyerdiscount_account_id: accountId,
          },
          path: 'curateddealbuyerdiscount.seat_ids',
        }
      );

      const seats = seatOptionsToCuratedDealBuyerDiscountSeats(data.optionsByPath);
      const seatsByReceivedSeatId = groupBy(seats, 'seat_id');

      const { acceptedItems, rejectedItems } = receivedSeatIds.reduce<ValidatedItems>(
        (acc, receivedSeatId) => {
          const seatsFoundInApi = seatsByReceivedSeatId[receivedSeatId];

          if (!seatsFoundInApi) {
            acc.rejectedItems.push(receivedSeatId);
            return acc;
          }

          for (const seat of seatsFoundInApi) {
            const isBlocked = isItemBlocked?.(seat);
            const shouldBeIgnored = !!itemsToIgnore[seat.id];

            const isAccepted = !isBlocked && !shouldBeIgnored;

            isAccepted ? acc.acceptedItems.push(seat) : acc.rejectedItems.push(seat.id);
          }

          return acc;
        },
        { acceptedItems: [], rejectedItems: [] }
      );

      return {
        acceptedItems,
        acceptedItemsStringExtractor: ({ id }) => id,
        error: t('Seats not found for the following Received Seat IDs and/or already taken for the following IDs'),
        rejectedItems,
      };
    },
    [accountId, queryClient, isItemBlocked, itemsToIgnore, t]
  );

  return {
    handleBulkValidate,
  };
};
